import { useState } from 'react';
import { Check, Copy, Download, FileText, Info } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import Modal from '../Modal';
import type { AtoAprovado } from '../../types/atosAdministrativos';

type Props = {
  ato: AtoAprovado | null;
  onClose: () => void;
};

function sanitizeFileName(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '_').trim();
}

export default function AtoDetailModal({ ato, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  if (!ato) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(ato.conteudo_final);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleExportWord = async () => {
    const paragraphs = ato.conteudo_final.split('\n').map(
      (line) =>
        new Paragraph({
          children: [new TextRun({ text: line, size: 24 })],
          spacing: { after: 120 },
        })
    );

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: ato.nome_ato, bold: true, size: 32 }),
              ],
              spacing: { after: 240 },
            }),
            ...paragraphs,
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFileName(ato.nome_ato)}.docx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen onClose={onClose} title={ato.nome_ato} size="xl">
      <div className="space-y-5">
        {/* Ações */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm btn-secondary"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copiado' : 'Copiar conteúdo'}
          </button>
          <button
            type="button"
            onClick={handleExportWord}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm btn-accent"
          >
            <Download className="h-4 w-4" />
            Exportar Word
          </button>
        </div>

        {/* Descrição */}
        <div>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-navy-200">
            <Info className="h-4 w-4" />
            Descrição
          </h4>
          <p className="mt-1 text-sm text-navy-600 dark:text-navy-300 leading-relaxed">
            {ato.descricao}
          </p>
        </div>

        {/* Requisitos mínimos */}
        {ato.requisitos_minimos && (
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-navy-200">
              <FileText className="h-4 w-4" />
              Requisitos mínimos
            </h4>
            <p className="mt-1 text-sm text-navy-600 dark:text-navy-300 leading-relaxed whitespace-pre-wrap">
              {ato.requisitos_minimos}
            </p>
          </div>
        )}

        {/* Conteúdo final */}
        <div>
          <h4 className="text-sm font-semibold text-navy-700 dark:text-navy-200">
            Conteúdo do modelo
          </h4>
          <div className="mt-2 max-h-96 overflow-y-auto rounded-lg border border-navy-200 bg-navy-50 p-4 text-sm leading-relaxed text-navy-800 whitespace-pre-wrap dark:border-navy-700 dark:bg-navy-800 dark:text-navy-100">
            {ato.conteudo_final}
          </div>
        </div>



        {/* Observações */}
        {ato.observacoes && (
          <div className="rounded-lg border border-navy-200 bg-amber-50/50 p-3 text-sm text-navy-600 dark:border-navy-700 dark:bg-amber-900/10 dark:text-navy-300">
            <span className="font-medium">Observações:</span> {ato.observacoes}
          </div>
        )}
      </div>
    </Modal>
  );
}
